import { ReactNode } from 'react';

/**
 * Converts a ReactNode to a string representation.
 *
 * @param {ReactNode} [node] - The ReactNode to convert.
 * @returns {string} The string representation of the ReactNode.
 */
export function reactNodeToString(node?: ReactNode): string {
  if (!node) {
    return '';
  }
  if (typeof node === 'string') {
    return node;
  }
  if (typeof node === 'number' || typeof node === 'boolean') {
    return `${node}`;
  }
  let result: string;
  try {
    result = getTextFromComponent(node as ReactEl);
  } catch (e) {
    result = '';
  }
  return result;
}

type ReactEl = { props?: { children?: string | ReactEl | (string | ReactEl)[] } };

/**
 * Extracts and concatenates text content from a React component.
 *
 * @param component - The React component from which to extract text. It should be of type `ReactEl`.
 * @returns The concatenated text content from the component's children. If the component has no children, returns an empty string.
 */
function getTextFromComponent(component: ReactEl): string {
  if (!Object.keys(component).length || !component.props?.children) {
    return '';
  }
  let result = '';
  const kids = Array.isArray(component.props?.children)
    ? [...(component.props?.children || [])]
    : [component.props?.children];
  while (kids.length) {
    const firstElem = kids.shift();
    if (typeof firstElem === 'string') {
      result += firstElem;
    } else if (Array.isArray(firstElem?.props?.children)) {
      kids.push(...(firstElem?.props?.children ?? []));
    } else if (firstElem?.props?.children) {
      kids.push(firstElem.props.children);
    }
  }

  return result;
}
